
import { SimulationResult, SimulationLog, Entity } from './types';

export const generateSimulationResult = (
  policyName: string,
  selectedEntities: string[]
): SimulationResult => {
  const actionsPerformed = Math.floor(Math.random() * selectedEntities.length * 2) + 1;
  const issuesDetected = Math.floor(Math.random() * selectedEntities.length);
  const complianceImprovement = Math.floor(Math.random() * 25) + 5;
  const complianceBefore = Math.floor(Math.random() * 60) + 30; // 30-90%
  const complianceAfter = Math.min(complianceBefore + complianceImprovement, 100);
  const riskReduction = Math.floor(Math.random() * 30) + 10; // 10-40%
  
  // Generate detailed summary
  const entityText = selectedEntities.length === 1 
    ? "1 team" 
    : `${selectedEntities.length} teams`;
    
  const summaryTexts = [
    `Executed governance policy "${policyName}" on ${entityText}, resulting in ${actionsPerformed} automatic remediation actions. Compliance score improved by ${complianceImprovement}%, with ${issuesDetected} issues requiring manual review.`,
    `Successfully applied "${policyName}" policy to ${entityText}. Performed ${actionsPerformed} automated governance actions and improved overall compliance by ${complianceImprovement}%. ${issuesDetected} compliance gaps identified for manual remediation.`,
    `Applied governance controls from "${policyName}" to ${entityText}. Automated ${actionsPerformed} remediation actions and increased compliance score from ${complianceBefore}% to ${complianceAfter}%. ${issuesDetected} issues flagged for manual review.`
  ];
  
  // Create more comprehensive action list
  const possibleActions = [
    "Updated team ownership information",
    "Applied retention policies to content",
    "Updated security classification metadata",
    "Remediated guest access permissions",
    "Fixed missing compliance attributes",
    "Applied governance labels to resources",
    "Updated inactive team settings",
    "Enforced data loss prevention policies",
    "Implemented conditional access rules",
    "Configured sensitivity labels",
    "Removed excessive permissions",
    "Updated external sharing settings",
    "Applied data residency controls"
  ];
  
  // Shuffle and take some actions
  const shuffledActions = [...possibleActions].sort(() => 0.5 - Math.random());
  const selectedActions = shuffledActions.slice(0, actionsPerformed);
  
  // Generate warnings if issues were detected
  const warningsList = issuesDetected > 0 ? [
    "Some guest users have excessive permissions",
    "External sharing settings not compliant with policy",
    "Missing data classification on sensitive content",
    "Team ownership needs to be updated",
    "Retention policies not properly configured"
  ].slice(0, issuesDetected) : [];
  
  return {
    policyName: policyName || 'Default Policy',
    executedAt: new Date().toISOString(),
    duration: `${Math.floor(Math.random() * 5) + 2}s`,
    entitiesProcessed: selectedEntities.length,
    actionsPerformed: actionsPerformed,
    issuesDetected: issuesDetected,
    complianceImprovement: complianceImprovement,
    complianceBefore: complianceBefore,
    complianceAfter: complianceAfter,
    summary: summaryTexts[Math.floor(Math.random() * summaryTexts.length)],
    details: `Found ${issuesDetected} issues and improved compliance by ${complianceImprovement}%.`,
    actionsList: selectedActions,
    warningsList: warningsList,
    riskReduction: riskReduction,
    impactScore: Math.floor(Math.random() * 5) + 3, // 3-8 out of 10
    recommendedActions: issuesDetected > 0 ? [
      "Review external sharing settings",
      "Update security contacts",
      "Configure data loss prevention policies",
      "Implement conditional access policies"
    ] : [],
    affectedResources: selectedEntities.map((_, i) => ({
      name: `Resource ${i+1}`,
      type: ['Document', 'Channel', 'Team', 'Site'][Math.floor(Math.random() * 4)],
      impact: ['High', 'Medium', 'Low'][Math.floor(Math.random() * 3)]
    })).slice(0, Math.min(5, selectedEntities.length))
  };
};

export const createLog = (
  type: 'info' | 'success' | 'warning' | 'error',
  message: string,
  entityId?: string
): SimulationLog => {
  return {
    type,
    message,
    timestamp: new Date(),
    entityId,
  };
};

export const getStepDuration = (speed: number): number => {
  switch (speed) {
    case 0.5: return 2000;
    case 2: return 500;
    default: return 1000;
  }
};

export const defaultSimulationSteps = [
  { id: 1, name: 'Initialize', description: 'Setting up execution environment' },
  { id: 2, name: 'Data Collection', description: 'Gathering entity data' },
  { id: 3, name: 'Policy Analysis', description: 'Evaluating against governance rules' },
  { id: 4, name: 'Decision Making', description: 'Determining actions to take' },
  { id: 5, name: 'Execute Actions', description: 'Applying governance actions' },
  { id: 6, name: 'Generate Report', description: 'Creating execution summary' },
];

export const defaultEntities: Entity[] = [
  { id: 'team1', name: 'Marketing Team', type: 'team', owner: 'Sarah Johnson', status: 'active', compliance: 72 },
  { id: 'team2', name: 'Development Group', type: 'team', owner: 'Michael Chen', status: 'inactive', compliance: 45 },
  { id: 'team3', name: 'HR Team', type: 'team', owner: 'Jessica Williams', status: 'active', compliance: 88 },
  { id: 'team4', name: 'Sales Department', type: 'team', owner: 'David Smith', status: 'active', compliance: 63 },
  { id: 'team5', name: 'Executive Committee', type: 'team', owner: 'Emma Rodriguez', status: 'active', compliance: 91 },
  { id: 'channel1', name: 'General', type: 'channel', teamName: 'HR Team', status: 'empty' },
  { id: 'viva1', name: 'Company News', type: 'viva', owner: 'Jessica Miller', status: 'active' },
  { id: 'group1', name: 'Finance Department', type: 'group', owner: 'Robert Williams', status: 'inactive', compliance: 51 },
];
